import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Modal, Slider, Tooltip, Typography } from '@mui/material';
import { InfoOutlinedIcon, MoreVertIcon, KeyboardArrowLeftIcon, KeyboardArrowRightIcon } from 'icons/materialIcons';
import Cropper from 'react-easy-crop';
import getCroppedImg from 'team/shared/utils/images/teamCropImageUtils';
import TeamAssetOptionsModal from './TeamAssetOptionsModal';

const TeamAssetsUploadCard = ({
    title,
    infoText,
    buttonText,
    useFileInput = true,
    helperText = 'or',
    helperSubText = 'drag and drop one here',
    enableCrop = false,
    cropAspect = 1,
    cropShape = 'rect',
    cropOutputShape = 'rectangle',
    cropMinZoom = 0.05,
    cropMaxZoom = 3,
    onCroppedImage,
    assets = [],
    onDeleteAsset,
    autoZoomOnLoad = false,
    initialZoom = 1,
    imagePreviewWidth = '6.5rem',
    imagePreviewAspectRatio = '1 / 1',
    imagePreviewRadius = '12px',
    imagePreviewHeight,
    savedAssetWidth = '13rem',
    savedAssetHeight = '13rem',
    fileInputRef,
    onActionClick
}) => {
    const [open, setOpen] = useState(false);
    const [image, setImage] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [objectUrl, setObjectUrl] = useState(null);
    const [selectedFileName, setSelectedFileName] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuAssetId, setMenuAssetId] = useState(null);
    const [currentPage, setCurrentPage] = useState(0);

    const itemsPerPage = 4;
    const hasAssets = assets.length > 0;
    const totalPages = Math.ceil(assets.length / itemsPerPage);
    const visibleAssets = useMemo(() => {
        const start = currentPage * itemsPerPage;
        return assets.slice(start, start + itemsPerPage);
    }, [assets, currentPage]);

    const handleImageChange = (event) => {
        if (!enableCrop) {
            return;
        }

        if (event.target.files && event.target.files[0]) {
            const file = URL.createObjectURL(event.target.files[0]);
            setObjectUrl(file);
            setImage(file);
            setSelectedFileName(event.target.files[0].name || '');
            setOpen(true);
            event.target.value = '';
        }
    };

    const handleClose = () => {
        setOpen(false);
        setImage(null);
        setObjectUrl(null);
        setZoom(1);
        setCrop({ x: 0, y: 0 });
    };

    const onCropComplete = useCallback((_, croppedAreaPixelsValue) => {
        setCroppedAreaPixels(croppedAreaPixelsValue);
    }, []);

    const calculateInitialZoom = useCallback((mediaWidth, mediaHeight) => {
        const containerWidth = window.innerWidth * 0.9;
        const containerHeight = window.innerHeight * 0.9;
        const widthRatio = containerWidth / mediaWidth;
        const heightRatio = containerHeight / mediaHeight;
        return Math.min(widthRatio, heightRatio);
    }, []);

    const handleMediaLoaded = useCallback((mediaSize) => {
        if (autoZoomOnLoad) {
            const zoomValue = calculateInitialZoom(mediaSize.width, mediaSize.height);
            setZoom(zoomValue);
        } else {
            setZoom(initialZoom);
        }
        setCrop({ x: 0, y: 0 });
    }, [autoZoomOnLoad, calculateInitialZoom, initialZoom]);

    const handleSave = useCallback(async () => {
        if (!image || !croppedAreaPixels) {
            return;
        }

        try {
            const fileName = `team-asset-${Date.now()}`;
            const croppedImageBlob = await getCroppedImg(
                image,
                croppedAreaPixels,
                cropOutputShape,
                fileName
            );
            if (onCroppedImage) {
                onCroppedImage(croppedImageBlob, selectedFileName);
            }
            handleClose();
        } catch (error) {
            console.error('Error cropping image:', error);
        }
    }, [image, croppedAreaPixels, cropOutputShape, onCroppedImage, selectedFileName]);

    useEffect(() => {
        return () => {
            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [objectUrl]);

    useEffect(() => {
        if (currentPage > 0 && currentPage >= totalPages) {
            setCurrentPage(Math.max(totalPages - 1, 0));
        }
    }, [currentPage, totalPages]);

    const handleMenuOpen = (event, assetId) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
        setMenuAssetId(assetId);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuAssetId(null);
    };

    const getAssetDisplayName = (asset) => {
        if (asset?.linkType) {
            return asset.linkType;
        }
        if (asset?.name) {
            return asset.name;
        }
        if (asset?.metadata?.originalFileName) {
            return asset.metadata.originalFileName;
        }
        try {
            const urlPart = asset?.url?.split('/o/')[1];
            if (!urlPart) return 'image';
            const decodedPath = decodeURIComponent(urlPart.split('?')[0]);
            const fileName = decodedPath.split('/').pop();
            return fileName || 'image';
        } catch (error) {
            return 'image';
        }
    };

    const getAssetSavedDate = (asset) => {
        if (!asset?.createdAt) return '';
        try {
            return new Date(asset.createdAt).toLocaleDateString('en-US', {
                month: '2-digit',
                day: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            return '';
        }
    };

    return (
        <Box
            sx={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: '1.25rem'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1rem' }}>
                <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>
                    {title}
                </Typography>
                <Tooltip title={infoText} arrow>
                    <InfoOutlinedIcon sx={{ fontSize: '1rem', color: '#828282' }} />
                </Tooltip>
            </Box>
            {hasAssets ? (
                <Box>
                    <Box sx={{ display: 'flex', gap: '2.5rem', alignItems: 'flex-start' }}>
                        <Box
                            sx={{
                                border: '2px dashed #E0E0E0',
                                borderRadius: '12px',
                                width: '13rem',
                                height: '13rem',
                                backgroundColor: '#f7f7f7',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                transition: 'all 0.2s',
                                flexShrink: 0,
                                '&:hover': {
                                    borderColor: '#29AEF8'
                                }
                            }}
                        >
                            <Button
                                variant="contained"
                                component={useFileInput ? 'label' : 'button'}
                                onClick={!useFileInput ? onActionClick : undefined}
                                sx={{
                                    backgroundColor: '#29AEF8',
                                    color: '#fff',
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    padding: '0.5rem 1.5rem',
                                    fontSize: '0.85rem',
                                    minWidth: 'unset',
                                    '&:hover': {
                                        backgroundColor: '#29AEF8'
                                    }
                                }}
                            >
                                {buttonText}
                                {useFileInput && (
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                    />
                                )}
                            </Button>
                            <Typography sx={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#4F4F4F' }}>
                                {helperText}
                            </Typography>
                            {helperSubText && (
                                <Typography sx={{ marginTop: '0.35rem', fontSize: '0.9rem', color: '#4F4F4F' }}>
                                    {helperSubText}
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: '2.5rem', flexWrap: 'nowrap' }}>
                            {visibleAssets.map((asset) => (
                                <Box key={asset._id} sx={{ width: savedAssetWidth, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            width: savedAssetWidth,
                                            height: savedAssetHeight,
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            transition: 'all 0.2s',
                                            '&:hover': {
                                                backgroundColor: '#f7f7f7'
                                            },
                                            '&:hover .asset-menu-button': {
                                                opacity: 1
                                            }
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: imagePreviewWidth,
                                                height: imagePreviewHeight,
                                                aspectRatio: imagePreviewAspectRatio,
                                                borderRadius: imagePreviewRadius,
                                                backgroundImage: `url(${asset.url})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center'
                                            }}
                                        />
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                bottom: '0.75rem',
                                                left: 0,
                                                width: '100%',
                                                textAlign: 'center',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '0 0.75rem'
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    color: '#4F4F4F',
                                                    fontWeight: 500,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    width: '100%'
                                                }}
                                            >
                                                {getAssetDisplayName(asset)}
                                            </Typography>
                                            {getAssetSavedDate(asset) && (
                                                <Typography
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        color: '#828282',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        width: '100%'
                                                    }}
                                                >
                                                    Saved on {getAssetSavedDate(asset)}
                                                </Typography>
                                            )}
                                        </Box>
                                        <IconButton
                                            className="asset-menu-button"
                                            onClick={(event) => handleMenuOpen(event, asset._id)}
                                            sx={{
                                                position: 'absolute',
                                                top: '6px',
                                                right: '6px',
                                                width: '28px',
                                                height: '28px',
                                                backgroundColor: '#ffffff',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                                                opacity: 0,
                                                transition: 'opacity 0.2s ease',
                                                '&:hover': {
                                                    backgroundColor: '#ffffff'
                                                }
                                            }}
                                        >
                                            <MoreVertIcon sx={{ fontSize: '1rem' }} />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
                            <Button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                                disabled={currentPage === 0}
                                sx={{
                                    textTransform: 'none',
                                    color: '#4F4F4F',
                                    minWidth: 'auto',
                                    padding: 0
                                }}
                                startIcon={<KeyboardArrowLeftIcon />}
                            >
                                Prev
                            </Button>
                            <Button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                                disabled={currentPage >= totalPages - 1}
                                sx={{
                                    textTransform: 'none',
                                    color: '#4F4F4F',
                                    minWidth: 'auto',
                                    padding: 0
                                }}
                                endIcon={<KeyboardArrowRightIcon />}
                            >
                                Next
                            </Button>
                        </Box>
                    )}
                </Box>
            ) : (
                <Box
                    sx={{
                        border: '2px dashed #E0E0E0',
                        borderRadius: '12px',
                        padding: '1.5rem',
                        minHeight: '14rem',
                        backgroundColor: '#f7f7f7',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        '&:hover': {
                            borderColor: '#29AEF8'
                        }
                    }}
                >
                    <Button
                        variant="contained"
                        component={useFileInput ? 'label' : 'button'}
                        onClick={!useFileInput ? onActionClick : undefined}
                        sx={{
                            backgroundColor: '#29AEF8',
                            color: '#fff',
                            borderRadius: '20px',
                            textTransform: 'none',
                            fontWeight: 600,
                            padding: '0.35rem 1.25rem',
                            '&:hover': {
                                backgroundColor: '#29AEF8'
                            }
                        }}
                    >
                        {buttonText}
                        {useFileInput && (
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                style={{ display: 'none' }}
                            />
                        )}
                    </Button>
                    <Typography sx={{ marginTop: '0.75rem', fontSize: '0.9rem', color: '#4F4F4F' }}>
                        {helperText}
                    </Typography>
                    {helperSubText && (
                        <Typography sx={{ marginTop: '0.35rem', fontSize: '0.9rem', color: '#4F4F4F' }}>
                            {helperSubText}
                        </Typography>
                    )}
                </Box>
            )}

            {enableCrop && (
                <Modal
                    open={open}
                    onClose={handleClose}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backdropFilter: 'blur(7px)',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)'
                    }}
                >
                    <Box
                        sx={{
                            outline: 'none',
                            width: '90vw',
                            maxWidth: '900px',
                            height: '90vh',
                            maxHeight: '700px'
                        }}
                    >
                        {image && (
                            <>
                                <Box
                                    sx={{
                                        position: 'relative',
                                        width: '100%',
                                        height: 'calc(100% - 100px)',
                                        marginBottom: 2
                                    }}
                                >
                                    <Cropper
                                        image={image}
                                        crop={crop}
                                        zoom={zoom}
                                        aspect={cropAspect}
                                        cropShape={cropShape}
                                        onCropChange={setCrop}
                                        onZoomChange={setZoom}
                                        onCropComplete={onCropComplete}
                                        onMediaLoaded={handleMediaLoaded}
                                        objectFit="contain"
                                        centerImage={true}
                                        showGrid={false}
                                        minZoom={cropMinZoom}
                                        maxZoom={cropMaxZoom}
                                        restrictPosition={false}
                                    />
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Slider
                                        value={zoom}
                                        onChange={(event, newValue) => setZoom(newValue)}
                                        min={cropMinZoom}
                                        max={cropMaxZoom}
                                        step={0.01}
                                        valueLabelDisplay="auto"
                                        defaultValue={1}
                                        sx={{ marginBottom: 2, width: '80%', margin: '0 auto 20px auto' }}
                                    />
                                    <Box>
                                        <Button onClick={handleClose} variant="contained" sx={{ marginRight: 2 }}>
                                            Cancel
                                        </Button>
                                        <Button onClick={handleSave} variant="contained" color="primary">
                                            Crop
                                        </Button>
                                    </Box>
                                </Box>
                            </>
                        )}
                    </Box>
                </Modal>
            )}

            <TeamAssetOptionsModal
                open={Boolean(menuAnchorEl)}
                onClose={handleMenuClose}
                anchorEl={menuAnchorEl}
                onDelete={async () => {
                    if (onDeleteAsset && menuAssetId) {
                        await onDeleteAsset(menuAssetId);
                    }
                    handleMenuClose();
                }}
            />
        </Box>
    );
};

export default TeamAssetsUploadCard;
